import MetricsBarChartEntry from './MetricBar';
import MetricsEntry from './MetricsEntry';
import MetricPieChart from './MetricPieChart';
import MetricLineChart, { LinePoint } from './MetricLineChart';
import { useAuth } from '@/providers/AuthProvider';
import config from '@/config';
import { useAmplifyApiClient } from '@/hooks/AmplifyApiClientHooks';
import { useEffect, useMemo, useState } from 'react';
import * as queries from '@/graphql/queries';
import { formatDateMetrics } from '@/utils/dateUtils';
import PopularPromptLine from './MetricShapes/PopularPromptLine';
import PopularPackLine from './MetricShapes/PopularPackLine';
import CustomPromptsLine from './MetricShapes/CustomPromptsLine';
import PopularPromptShape from './MetricShapes/PopularPromptShape';
import PopularPackShape from './MetricShapes/PopularPackShape';
import CustomPromptsShape from './MetricShapes/CustomPromptsShape';
import { gamePackList } from '@/graphql/queries';
import { GamePackPalette } from '@/API';
import { GamePackPaletteOptions, getSelectedGamePackPalette } from '@/utils/gamePacks';
import FreeTrialBanner from '../FreeTrial/FreeTrialBanner';

type MetricsDto = {
  updatedAt?: string;
  sentimentIncrease: number;
  sentimentCount: number;
  totalPlayers: number;
  gamesHosted: number;
  questionScore: { label: string; value: number }[];
  reactionScore: { label: string; value: number }[];
  totalHosts?: number;
  storiesShared?: number;
  medianRoundTime?: number;
  popularPrompt?: string;
  popularGamePack?: string;
  avgPlayersPerGame?: number;
  avgRoundsPerGame?: number;
  avgGameLengthMin?: number;
  activityTimeline?: { label: string; value: number }[];
  customPromptsCount?: number;
};

type MetricsProps = {
  tenantId: string;
  hostId?: string;
};

function Metrics({ tenantId, hostId }: MetricsProps) {
  const { groups } = useAuth();
  const client = useAmplifyApiClient();

  const isSuperAdmin = groups.includes(config.admin.superAdminGroup);
  const isAdmin = groups.includes(config.admin.adminGroup);
  const isHost = groups.includes(config.admin.hostGroup);

  const canViewAdmin = isSuperAdmin || isAdmin;
  const canViewHost = isHost && !canViewAdmin;

  const [metrics, setMetrics] = useState<MetricsDto | null>(null);
  const [popularPackPalette, setPopularPackPalette] = useState<GamePackPaletteOptions | null>(null);
  const [popularPromptPalette, setPopularPromptPalette] = useState<GamePackPaletteOptions | null>(
    null
  );

  useEffect(() => {
    let cancelled = false;
    const fetchMetrics = async () => {
      try {
        if (canViewAdmin) {
          const res = (await client.graphql({
            query: queries.metricsByTenant,
            variables: { tenantId }
          })) as any;
          const data: MetricsDto | null = res?.data?.metricsByTenant ?? null;
          if (!cancelled) setMetrics(data);
        } else if (canViewHost && hostId) {
          const res = (await client.graphql({
            query: queries.metricsByHost,
            variables: { hostId }
          })) as any;
          const data: MetricsDto | null = res?.data?.metricsByHost ?? null;
          if (!cancelled) setMetrics(data);
        } else {
          if (!cancelled) setMetrics(null);
        }
      } catch (err) {
        console.error('Failed to load metrics', err);
        if (!cancelled) setMetrics(null);
      }
    };
    fetchMetrics();
    return () => {
      cancelled = true;
    };
  }, [tenantId, hostId, client, canViewAdmin, canViewHost]);

  // Load palettes for the most popular prompt and game pack separately
  useEffect(() => {
    const loadPalettes = async () => {
      try {
        const res = await client.graphql({ query: gamePackList });
        const packs = res.data?.gamePackList ?? [];

        // Popular game pack palette
        const packName = metrics?.popularGamePack;
        if (packName) {
          const packMatch = packs.find((p) => p?.name === packName);
          if (packMatch?.palette != null) {
            setPopularPackPalette(getSelectedGamePackPalette(packMatch.palette) ?? null);
          }
        }

        // Popular prompt palette (find the pack that contains this prompt)
        const promptText = metrics?.popularPrompt;
        if (promptText) {
          let promptPackPalette: GamePackPalette | null = null;
          for (const p of packs) {
            if (p?.prompts?.some((pr) => pr?.text === promptText)) {
              promptPackPalette = p.palette ?? null;
              break;
            }
          }

          if (promptPackPalette != null) {
            setPopularPromptPalette(getSelectedGamePackPalette(promptPackPalette) ?? null);
          }
        }
      } catch {
        setPopularPackPalette(null);
        setPopularPromptPalette(null);
      }
    };
    loadPalettes();
  }, [client, metrics?.popularGamePack, metrics?.popularPrompt]);

  const questionLabelMap: Record<string, string> = useMemo(
    () => ({
      connected: 'Feel More Connected',
      participation: 'Enjoyed Participating',
      creativity: 'Feel More Creative'
    }),
    []
  );

  return (
    <div className="w-full h-full overflow-auto">
      <div className="w-900 mx-auto content-wrapper">
        <FreeTrialBanner />
        <h1 className="text-h1 text-secondary-navy-500 mb-4">Dashboard Overview</h1>
        {metrics && metrics.gamesHosted > 0 ? (
          <>
            {metrics.updatedAt ? (
              <p className="text-body text-neutral-400">
                Last updated {formatDateMetrics(metrics.updatedAt)}
              </p>
            ) : null}

            <h2 className="mb-24 mt-40 text-neutral-500 text-h2">Players Sentiment & Engagement</h2>
            <div className="flex w-full gap-24 flex-wrap">
              <MetricsBarChartEntry
                label={'Player Sentiment'}
                subLabel={`derived from ${metrics.sentimentCount} players of ${metrics.totalPlayers} players`}
                colorOverrides={['#99d9d9', '#41797b']}
                data={[{ label: 'Feel Better', value: metrics.sentimentIncrease }]}
              />
              <MetricsBarChartEntry
                label={'How Players Feel'}
                subLabel={`derived from ${metrics.totalPlayers} players across ${metrics.gamesHosted} games`}
                data={(metrics.questionScore || [])
                  .slice()
                  .sort((a, b) => {
                    const order = ['connected', 'participation', 'creativity'];
                    return order.indexOf(a.label) - order.indexOf(b.label);
                  })
                  .map((entry) => ({
                    label: questionLabelMap[entry.label] || entry.label,
                    value: entry.value
                  }))}
              />
              <MetricPieChart
                label={'Player Reaction'}
                subLabel={`derived from ${metrics.totalPlayers} players across ${metrics.gamesHosted} games`}
                data={(metrics.reactionScore || []).map((r) => ({
                  label: r.label,
                  value: r.value
                }))}
              />
              <MetricLineChart
                label={'Organizational Activity'}
                subLabel={'Games played over the last 7 days'}
                data={(metrics.activityTimeline as LinePoint[]) || []}
              />
            </div>

            <div className="mt-56">
              <h2 className="mb-4 text-neutral-500 text-h2 mb-24">Overview</h2>
              <div className="flex w-full gap-24">
                {canViewAdmin && (
                  <MetricsEntry
                    data={metrics.totalHosts?.toString()}
                    label={'Users Hosting Games'}
                    tooltip={'Unique hosts who ran a game'}
                  />
                )}
                <MetricsEntry
                  data={String(metrics.gamesHosted)}
                  label={'Games Hosted'}
                  tooltip={'Total number of games run'}
                />
                <MetricsEntry
                  data={String(metrics.totalPlayers)}
                  label={'Players'}
                  tooltip={'Unique number of players'}
                />
                <MetricsEntry
                  data={metrics.storiesShared?.toString()}
                  label={'Stories Shared'}
                  tooltip={'Total stories across games'}
                />
              </div>
            </div>

            <div className="mt-56">
              <h2 className="mb-4 text-neutral-500 text-h2 mb-24">Game Metrics</h2>
              <div className="flex w-full gap-24">
                <MetricsEntry
                  data={(metrics.avgPlayersPerGame ?? 0).toString()}
                  label={'Average Players/Game'}
                  tooltip={'Mean average, exclude outliers'}
                />
                <MetricsEntry
                  data={(metrics.avgRoundsPerGame ?? 0).toString()}
                  label={'Average Rounds/Game'}
                  tooltip={'Mean average across games'}
                />
                <MetricsEntry
                  data={(metrics.avgGameLengthMin ?? 0).toString()}
                  label={'Average Game Length'}
                  unit=" min"
                  tooltip={'Mean minutes per game'}
                />
                <MetricsEntry
                  data={(metrics.medianRoundTime ?? 0).toString()}
                  label={'Median Round Time'}
                  unit=" min"
                  tooltip={'Median minutes per round'}
                />
              </div>
            </div>

            <div className="mt-56">
              <h2 className="mb-4 text-neutral-500 text-h2 mb-24">Content Creation</h2>
              <div className="flex h-190 items-start gap-20 self-stretch w-full">
                <div
                  className={`
                  flex-[1_0_0] rounded-xs p-20 h-full ${popularPromptPalette?.colors.bg ?? ''}
                  flex flex-col items-center justify-center text-center
                  elevated relative overflow-hidden
                `}
                >
                  <PopularPromptLine
                    className={`absolute bottom-0 left-10 z-0 ${popularPromptPalette?.colors.shapes ?? ''}`}
                    aria-hidden
                  />
                  <PopularPromptShape
                    className={`absolute top-0 right-0 z-0 ${popularPromptPalette?.colors.shapes ?? ''}`}
                    aria-hidden
                  />
                  <div className="text-h2 pb-8 relative z-1">{`"${metrics.popularPrompt}"`}</div>
                  <div className="text-sm text-neutral-500 relative z-1">Most Popular Prompt</div>
                </div>
                <div
                  className={`
                  flex-[1_0_0] rounded-xs p-20 h-full ${popularPackPalette?.colors.bg ?? ''}
                  flex flex-col items-center justify-center text-center
                  elevated relative overflow-hidden
                `}
                >
                  <PopularPackLine
                    className={`absolute bottom-0 left-20 z-0 ${popularPackPalette?.colors.shapes ?? ''}`}
                    aria-hidden
                  />
                  <PopularPackShape
                    className={`absolute top-0 right-0 z-0 ${popularPackPalette?.colors.shapes ?? ''}`}
                    aria-hidden
                  />
                  <div className="text-h2 pb-8 relative z-1">{metrics.popularGamePack}</div>
                  <div className="text-sm text-neutral-500 relative z-1">
                    Most Popular Game Pack
                  </div>
                </div>
                <div
                  className={`
                  flex-[1_0_0] rounded-xs p-20 h-full bg-accent-1-100
                  flex flex-col items-center justify-center text-center
                  elevated relative overflow-hidden
                `}
                >
                  <CustomPromptsLine className="absolute bottom-0 left-0 z-0" aria-hidden />
                  <CustomPromptsShape className="absolute top-0 right-0 z-0" aria-hidden />
                  <div className="text-h1 pb-8 font-extrabold relative z-1">
                    {metrics.customPromptsCount ?? 0}
                  </div>
                  <div className="text-sm text-neutral-500 relative z-1">
                    Custom Prompts Created
                  </div>
                  <div className="text-sm text-neutral-400 relative z-1">
                    (All games, played & not played)
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="mb-24"></div>
            <div>Metrics will be generated once games are played</div>
          </>
        )}
      </div>
    </div>
  );
}

export default Metrics;
